///
/// EarthBrowser
///
/// Created by Matt Giger on 10/28/2026
/// Copyright (c) 2026 Geodata Labs
///

import {
	ContinuousGestureRecognizer,
	GestureState,
	type GestureEventData,
	type GestureRecognizerConfig,
	type TouchPoint
} from "./GestureRecognizer"

export interface TapDragGestureEventData extends GestureEventData {
	/** Where the second touch landed (canvas coordinates) */
	startX: number
	startY: number
	/** Pointer travel since the second touch landed */
	translationX: number
	translationY: number
	/** PointerEvent.pointerType of the dragging pointer */
	pointerType: string
}

export interface TapDragGestureConfig extends GestureRecognizerConfig {
	/** Longest press that still counts as the first tap (ms, default 300) */
	maxTapDuration?: number
	/** Most the first tap may move, and twice this is how near the second touch must land (px, default 20) */
	maxTapMovement?: number
	/** Longest gap between the first tap lifting and the second touch landing (ms, default 350) */
	maxIntervalBetweenTaps?: number
	/** Second-touch travel before the drag begins (px, default 3) */
	minimumMovement?: number
}

/**
 * Tap, then press and drag with the same finger: the one-handed zoom gesture
 * of phone map apps (also works with a mouse as double-click-and-drag).
 *
 * The recognizer remembers a quick, still first tap across touch sessions
 * (staying Possible, as a multi-tap does) and arms on a second touch that
 * lands soon after and nearby. It begins once that touch travels
 * `minimumMovement`, which must be below the pan's threshold: in the arena
 * the first recognizer to begin owns the touch. If the second touch lifts
 * without travelling, it fails, leaving a plain double tap to the double-tap
 * recognizer.
 */
export class TapDragGestureRecognizer extends ContinuousGestureRecognizer<TapDragGestureEventData> {
	private maxTapDuration: number
	private maxTapMovement: number
	private maxIntervalBetweenTaps: number
	private minimumMovement: number

	// Remembered first tap (survives the session boundary)
	private firstTapTime = 0
	private firstTapLocation?: { x: number, y: number }

	// Current touch
	private armed = false
	private touchStartTime = 0
	private startLocation?: { x: number, y: number }
	private pointerType = "mouse"

	constructor(config: TapDragGestureConfig = {}) {
		super({ ...config, minimumTouches: 1, maximumTouches: 1 })
		this.maxTapDuration = config.maxTapDuration ?? 300
		this.maxTapMovement = config.maxTapMovement ?? 20
		this.maxIntervalBetweenTaps = config.maxIntervalBetweenTaps ?? 350
		this.minimumMovement = config.minimumMovement ?? 3
	}

	get gestureType(): string {
		return "doubleTap"
	}

	handleTouchStart(touches: TouchPoint[]): void {
		if (!this._enabled) return

		touches.forEach(t => this.touches.set(t.id, t))

		// A second finger ends this gesture (it's a pinch now)
		if (this.touchCount > 1) {
			if (this.isActive()) {
				this.cancel()
			} else {
				this.fail()
			}
			this.forgetFirstTap()
			return
		}

		const now = performance.now()
		const location = this.getCentroid()
		this.touchStartTime = now
		this.startLocation = location
		this.pointerType = touches[0]?.pointerType ?? "mouse"

		const first = this.firstTapLocation
		this.armed = !!first
			&& now - this.firstTapTime <= this.maxIntervalBetweenTaps
			&& Math.hypot(location.x - first.x, location.y - first.y) <= this.maxTapMovement * 2
		if (!this.armed) {
			this.forgetFirstTap()
		}
	}

	handleTouchMove(touches: TouchPoint[]): void {
		if (!this._enabled || this.isTerminal()) return

		touches.forEach(t => {
			const existing = this.touches.get(t.id)
			if (existing) {
				existing.x = t.x
				existing.y = t.y
			}
		})
		if (!this.startLocation) return

		if (this.isActive()) {
			this.change(this.createEventData(GestureState.Changed))
			return
		}

		const location = this.getCentroid()
		const distance = Math.hypot(location.x - this.startLocation.x, location.y - this.startLocation.y)
		if (this.armed) {
			if (distance >= this.minimumMovement) {
				this.begin(this.createEventData(GestureState.Began))
			}
		} else if (distance > this.maxTapMovement) {
			// The first touch is a drag, not a tap
			this.fail()
		}
	}

	handleTouchEnd(touches: TouchPoint[]): void {
		if (!this._enabled || this.isTerminal()) return

		if (this.isActive()) {
			const data = this.createEventData(GestureState.Ended)
			touches.forEach(t => this.touches.delete(t.id))
			this.end(data)
			this.forgetFirstTap()
			return
		}

		touches.forEach(t => this.touches.delete(t.id))

		if (this.armed) {
			// Second touch lifted without travelling: a plain double tap
			this.armed = false
			this.forgetFirstTap()
			this.fail()
			return
		}

		// First touch: remember it as a tap if it was quick and still, and
		// stay Possible so the memory survives the session boundary
		const now = performance.now()
		const start = this.startLocation
		const lastTouch = touches[0]
		const moved = start && lastTouch ? Math.hypot(lastTouch.x - start.x, lastTouch.y - start.y) : Infinity
		if (start && now - this.touchStartTime <= this.maxTapDuration && moved <= this.maxTapMovement) {
			this.firstTapTime = now
			this.firstTapLocation = start
			this.startLocation = undefined
		} else {
			this.fail()
		}
	}

	handleTouchCancel(): void {
		this.forgetFirstTap()
		this.cancel()
	}

	private forgetFirstTap(): void {
		this.firstTapTime = 0
		this.firstTapLocation = undefined
	}

	protected createEventData(state: GestureState): TapDragGestureEventData {
		const start = this.startLocation ?? { x: 0, y: 0 }
		const location = this.touches.size > 0 ? this.getCentroid() : start
		return {
			type: "doubleTap",
			state,
			timestamp: performance.now(),
			x: location.x,
			y: location.y,
			startX: start.x,
			startY: start.y,
			translationX: location.x - start.x,
			translationY: location.y - start.y,
			pointerType: this.pointerType,
			ownerHits: this.getOwnerHits()
		}
	}

	override reset(): void {
		super.reset()
		this.armed = false
		this.touchStartTime = 0
		this.startLocation = undefined
		this.forgetFirstTap()
	}
}
